import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { PaymentDetails } from 'common/services/pay-service';
import { BackFromProfile, GlobalState, RequestError } from './types';

import { AstroCustomHeaders } from 'common/helpers/cookiesHandler';
import { RootState } from 'common/store/store';
import { Country } from 'common/types/global';

const initCountry: Country = {
  iso: 'IN',
  name: 'India',
  currencyCode: 'INR',
  callingCode: '91',
  phoneHint: '9xxxxxxxxx',
  minLength: 10,
  language: 'en',
  voiceCallEnabled: false,
  documentTypeOptions: [
    {
      documentType: 'Aadhaar',
      value: 'Aadhaar',
    },
  ],
  states: [],
  tax: 0,
  fidelityEnabled: true,
  challengeEnabled: true,
  prepaidCardEnabled: true,
  referralEnabled: true,
  refundCheck: true,
  refundEnabled: true,
};

const initialState: GlobalState = {
  countries: [] as Country[],
  country: initCountry,
  token: '',
  requiredFields: [],
  isSdk: false,
  mobileSdk: false,
};

export const UnitedKingdomIso = 'UK';
export const GreatBritainIso = 'GB';

export const countryReducer = (countries: Country[], countryIso: string) => {
  const country = countries.find(
    ({ iso }) => iso === countryIso || (iso === UnitedKingdomIso && countryIso === GreatBritainIso),
  );
  return !!country ? country : initCountry;
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    resetStore: (state) => {
      state = initialState;
      return state;
    },
    setCountries: (state, action: PayloadAction<Country[]>) => {
      state.countries = action.payload;
    },
    setCountry: (state, action: PayloadAction<Country>) => {
      state.country = action.payload;
    },
    setCountryByCode: (state, action: PayloadAction<string>) => {
      state.country = countryReducer(state.countries, action.payload);
    },
    handleError: (state, action: PayloadAction<RequestError>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = undefined;
    },
    setRequiredFields: (state, action: PayloadAction<string[]>) => {
      state.requiredFields = action.payload;
    },
    setBackFromProfile: (state, action: PayloadAction<BackFromProfile>) => {
      state.backFromProfile = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setCookies: (state, action: PayloadAction<Record<AstroCustomHeaders, string>>) => {
      state.cookies = action.payload;
    },
    setIsSdk: (state, action: PayloadAction<boolean>) => {
      state.isSdk = action.payload;
    },
    setMobileSdk: (state, action: PayloadAction<boolean>) => {
      state.mobileSdk = action.payload;
    },
    setPayment: (state, action: PayloadAction<PaymentDetails>) => {
      state.payment = action.payload;
    },
    setWhiteLabel: (state, action: PayloadAction<boolean>) => {
      state.whiteLabel = action.payload;
    },
  },
});

// Selectors
const getGlobal = (state: RootState): GlobalState => state.global;
export const getError = createSelector(getGlobal, (global) => global.error);
export const getCountries = createSelector(getGlobal, (global) => global.countries);
export const getCountry = createSelector(getGlobal, (global) => global.country);
export const getToken = createSelector(getGlobal, (global) => global.token);
export const getRequiredFields = createSelector(getGlobal, (global) => global.requiredFields);
export const getBackFromProfile = createSelector(getGlobal, (global) => global.backFromProfile);
export const getCookies = createSelector(getGlobal, (global) => global.cookies);
export const getIsSdk = createSelector(getGlobal, (global) => global.isSdk);
export const getMobileSdk = createSelector(getGlobal, (global) => global.mobileSdk);
export const getPayment = createSelector(getGlobal, (global) => global.payment);
export const getWhiteLabel = createSelector(getGlobal, (global) => global.whiteLabel);

const { actions, reducer } = globalSlice;

export const {
  resetStore: globalReset,
  setToken,
  setRequiredFields,
  setBackFromProfile,
  setCountries,
  setCountry,
  setCountryByCode,
  setCookies,
  setPayment,
  setIsSdk,
  setMobileSdk,
  setWhiteLabel,
  handleError,
  clearError,
} = actions;

export default reducer;
